import { Location, matchPath } from 'react-router-dom'
import { AUTH_LINKS, AUTH_TOKEN, BILL_VIEW_ROUTE_WITH_TOKEN, TOKEN_INVALID } from '../constants'

/**
 * Server clock minus browser clock, in ms, measured when the session started. The token's
 * `exp` is server time, so without this a browser clock that runs fast or slow would end
 * the session early or late.
 */
export const SESSION_CLOCK_SKEW = 'helmer_session_clock_skew'

/**
 * Why the last session ended, left behind by `endSession` so other open tabs, which only
 * see the token disappear, can tell the user the same thing.
 */
export const SESSION_END_REASON = 'helmer_session_end_reason'

/** Dispatched on `window` by `endSession`; SessionWatcher turns it into the redirect. */
export const SESSION_ENDED_EVENT = 'helmer:session-ended'

/** `code` the API puts on the 401 for a token that has outlived its session. */
export const SESSION_EXPIRED_CODE = 'SESSION_EXPIRED'

export enum SessionEndReason {
  EXPIRED = 'expired',
  /** The API refused a token that has not expired, e.g. the user was deactivated. */
  INVALID = 'invalid',
  SIGNED_OUT = 'signed-out'
}

export type SessionEndedDetail = { reason: SessionEndReason }

/**
 * Router state handed to the login page. `email` is whose session ended, so the page they
 * were on is only reopened for that same user.
 */
export type LoginRouteState = { from?: Location; sessionEnded?: SessionEndReason; email?: string }

/** Pages that work without a session; ending one there must not pull the visitor to login. */
const PUBLIC_PATHS = [
  AUTH_LINKS.LOGIN_LINK,
  AUTH_LINKS.SET_PASSWORD,
  AUTH_LINKS.RESET_PASSWORD,
  AUTH_LINKS.FORGET_PASSWORD_LINK,
  BILL_VIEW_ROUTE_WITH_TOKEN
]

export const isPublicPath = (pathname: string) => PUBLIC_PATHS.some(path => matchPath(path, pathname))

/**
 * Where to go after signing in: the page the session ended on, as long as it is a page of
 * this app, it is not a sign-in page, and the same user is signing back in.
 */
export const returnPathAfterLogin = (state: unknown, email: string): string | null => {
  const { from, email: endedFor } = (state ?? {}) as LoginRouteState
  const pathname = from?.pathname

  if (!pathname || !pathname.startsWith('/') || pathname.startsWith('//') || isPublicPath(pathname)) return null
  if (endedFor && endedFor.trim().toLowerCase() !== email.trim().toLowerCase()) return null

  return `${pathname}${from.search ?? ''}${from.hash ?? ''}`
}

type TokenClaims = { iat?: number; exp?: number }

/** The token's payload, or null when it is not a readable JWT. The signature is the API's concern. */
export const readTokenClaims = (token: string): TokenClaims | null => {
  try {
    const payload = token.split('.')[1]
    const base64 = payload.replace(/-/g, '+').replace(/_/g, '/')
    return JSON.parse(atob(base64.padEnd(base64.length + ((4 - (base64.length % 4)) % 4), '=')))
  } catch {
    return null
  }
}

const clockSkew = (): number => Number(localStorage.getItem(SESSION_CLOCK_SKEW)) || 0

/** Current server time as best the browser can tell. */
export const serverNow = (): number => Date.now() + clockSkew()

/**
 * Ms left before the token expires, or null when it carries no `exp`. Tokens issued before
 * the API set an expiry have none; the API still ends them, and its 401 is what logs out.
 */
export const msUntilExpiry = (token: string): number | null => {
  const exp = readTokenClaims(token)?.exp
  return typeof exp === 'number' ? exp * 1000 - serverNow() : null
}

/** How long the session lasts in all, from sign-in to expiry, or null when the token does not say. */
export const sessionLifetime = (token: string): number | null => {
  const claims = readTokenClaims(token)
  return typeof claims?.iat === 'number' && typeof claims?.exp === 'number' ? (claims.exp - claims.iat) * 1000 : null
}

export const isTokenExpired = (token: string): boolean => {
  const remaining = msUntilExpiry(token)
  return remaining !== null && remaining <= 0
}

/** The token as stored, expired or not. Only for code that has to see an expired one. */
export const getStoredToken = () => localStorage.getItem(AUTH_TOKEN)

/** The token, or null once it has expired, so an ended session reads exactly like no session. */
export const getToken = () => {
  const token = getStoredToken()
  return token && !isTokenExpired(token) ? token : null
}

/**
 * True for the API's refusal of the session token itself. Other 401s, such as an invalid
 * link on the public bill page, are about something else and must not end the session.
 */
export const isSessionRejection = (body: unknown): body is { message: string; code?: string } =>
  (body as { message?: unknown } | null)?.message === TOKEN_INVALID

/** Why the API's refusal ended the session; the stored token's own expiry covers a request sent without it. */
export const rejectionReason = (code?: string): SessionEndReason => {
  const token = getStoredToken()
  return code === SESSION_EXPIRED_CODE || (token && isTokenExpired(token))
    ? SessionEndReason.EXPIRED
    : SessionEndReason.INVALID
}

/** Stores the token the API just issued, along with how far this browser's clock is off. */
export const startSession = (token: string) => {
  localStorage.removeItem(SESSION_END_REASON)
  localStorage.setItem(AUTH_TOKEN, token)
  const iat = readTokenClaims(token)?.iat

  if (typeof iat === 'number') {
    localStorage.setItem(SESSION_CLOCK_SKEW, String(iat * 1000 - Date.now()))
  } else {
    localStorage.removeItem(SESSION_CLOCK_SKEW)
  }
}
